import {
  AngebotsId,
  GlobalActions,
  MasterProduktData,
  NachlassReceived,
  OVERWRITE_ZUSATZOPTION,
  PreiseStreckeErmittelt,
  PreisverzeichnisChanged,
  RbdMaxReceived,
  TARIF_CHECKED,
  ZUSATZOPTION_CHECKED,
  ZUSATZOPTION_UNCHECKED,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  Gefahr,
  IPriceable,
  Leistung,
  LeistungEigenschaft,
  Produkt,
  Produktlinie,
  TaasTarifrisikoschluessel,
  Vorbelegungdaten,
  ZusatzoptionItem,
} from '@ps20-frontend/frontend-models';
import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { Produktlinienname, SalesProduktVariante, VorvertragBaustein } from '@ps20/bff-models';

export const produktReducer = function (
  sectionState: Produkt = null,
  action: AnyAction,
  appId: App,
  angebotsId?: string,
): Produkt {
  //NOSONAR
  switch (action.type) {
    case GlobalActions.PRODUKT_RECEIVED: {
      const actionPayload: MasterProduktData = action.payload;
      if (actionPayload.appId === appId && actionPayload.angebotsId === angebotsId) {
        return cloneDeep(actionPayload.produktData);
      }
      return sectionState;
    }
    case GlobalActions.PRODUKT_RECEIVED_AFTER_SAVE: {
      const actionPayload: MasterProduktData = action.payload;
      if (actionPayload.appId === appId && actionPayload.angebotsId === angebotsId) {
        const updatedProduktlinien: Produktlinie[] = [];
        sectionState.produktlinien.forEach(
          (produktlinie, index) =>
            (updatedProduktlinien[index] = updateZusatzoptionenAfterSave(
              produktlinie,
              actionPayload.produktData.produktlinien.find(
                produktlinieProdukt => produktlinieProdukt.produktName === produktlinie.produktName,
              ),
            )),
        );
        return { ...sectionState, produktlinien: updatedProduktlinien };
      }
      return sectionState;
    }
    case TARIF_CHECKED: {
      const newState = cloneDeep(sectionState);
      updateTarifItemSelected(newState.produktlinien, action.payload.index);
      return newState;
    }
    case OVERWRITE_ZUSATZOPTION: {
      const changes: Array<{ name: string; status: boolean }> = action.payload;
      const updatedProduktlinien: Produktlinie[] = sectionState.produktlinien;
      changes.forEach(change => {
        sectionState.produktlinien.forEach(
          (produktlinie, index) =>
            (updatedProduktlinien[index] = updateZusatzoptionen(
              produktlinie,
              change.name,
              change.status,
            )),
        );
      });
      return { ...sectionState, produktlinien: updatedProduktlinien };
    }
    case ZUSATZOPTION_CHECKED: {
      const updatedProduktlinien: Produktlinie[] = [];
      sectionState.produktlinien.forEach(
        (produktlinie, index) =>
          (updatedProduktlinien[index] = updateZusatzoptionen(
            produktlinie,
            action.payload.name,
            true,
          )),
      );
      return { ...sectionState, produktlinien: updatedProduktlinien };
    }
    case ZUSATZOPTION_UNCHECKED: {
      const updatedProduktlinien: Produktlinie[] = [];
      sectionState.produktlinien.forEach(
        (produktlinie, index) =>
          (updatedProduktlinien[index] = updateZusatzoptionen(
            produktlinie,
            action.payload.name,
            false,
          )),
      );
      return { ...sectionState, produktlinien: updatedProduktlinien };
    }
    case GlobalActions.RBD_MAX_RECEIVED: {
      const actionPayload: RbdMaxReceived = action.payload;
      const newState = cloneDeep(sectionState);
      if (
        newState !== null &&
        actionPayload.appId === appId &&
        actionPayload.angebotsId === angebotsId
      ) {
        const selectedProduktlinie = getSelectedProduktlinie(newState);
        selectedProduktlinie.rbdMax = actionPayload.rbdMax;
        return newState;
      }

      return sectionState;
    }
    case GlobalActions.NACHLASS_RECEIVED: {
      const nachlass: NachlassReceived = action.payload;
      if (nachlass.appId === appId && nachlass.angebotsId === angebotsId) {
        const selectedProduktlinie = getSelectedProduktlinie(sectionState);
        const produktLinieIndex = sectionState.produktlinien.indexOf(selectedProduktlinie);
        return {
          ...sectionState,
          produktlinien: sectionState.produktlinien.map((linie, index) => {
            if (index === produktLinieIndex) {
              return {
                ...linie,
                nachlass: nachlass.aktionsNachlass,
                kooperation: nachlass.code,
              };
            }
            return linie;
          }),
        };
      }
      return sectionState;
    }
    case GlobalActions.PREISE_STRECKE_ERMITTELT: {
      const actionPayload: PreiseStreckeErmittelt = action.payload;
      if (actionPayload.appId === appId && actionPayload.angebotsId === angebotsId) {
        return {
          ...sectionState,
          produktlinien: actionPayload.tarifierteProduktlinien,
        };
      }
      return sectionState;
    }

    case GlobalActions.PREISVERZEICHNIS_CHANGED: {
      const newState: Produkt = cloneDeep(sectionState);
      const actionPayload: PreisverzeichnisChanged = action.payload;
      if (newState !== null) {
        newState.produktlinien.forEach(
          produktLinie => (produktLinie.preisverzeichnis = actionPayload.preisverzeichnis),
        );
        return newState;
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: Vorbelegungdaten = action.payload;
      if (
        actionPayload.forApp === appId &&
        actionPayload.angebotsId === angebotsId &&
        sectionState !== null &&
        sectionState?.produktlinien?.length > 0
      ) {
        const newState = cloneDeep(sectionState);
        determineSelectedProduktlinie(actionPayload, newState);
        determineSelectedBausteine(actionPayload, newState);
        return newState;
      }
      return sectionState;
    }
    case GlobalActions.SHOP_TARIFIERUNG_BAUSTEINPREISE_CHANGED: {
      const bausteinPreisListe = action.payload;
      return {
        ...sectionState,
        produktlinien: sectionState.produktlinien.map(linie => {
          if (linie.selected) {
            return {
              ...linie,
              leistungen: updateBausteinpreise(linie.leistungen, bausteinPreisListe),
              zusatzoptionen: updateBausteinpreise(linie.zusatzoptionen, bausteinPreisListe),
              gefahren: updateBausteinpreise(linie.gefahren, bausteinPreisListe),
            };
          }
          return linie;
        }),
      };
    }
    default:
      return sectionState;
  }
};

function updateBausteinpreise<T extends IPriceable>(
  bausteine: T[],
  bausteinPreisListe: SalesProduktVariante['bausteinPreise'],
): T[] {
  return bausteine.map(baustein => {
    const foundNewPriceItam = bausteinPreisListe.find(
      item => item.taasTarifrisikoschluessel === baustein.name,
    );
    if (foundNewPriceItam) {
      return {
        ...baustein,
        bruttopreis: foundNewPriceItam.bruttopreisFuerZahlweise,
        nettopreisProJahr: foundNewPriceItam.jahresNetto,
        cpStern: foundNewPriceItam.cpStern,
      };
    }
    return baustein;
  });
}

function updateTarifItemSelected(produktlinien: Produktlinie[], index: number): void {
  for (let i = 0; i < produktlinien.length; i++) {
    produktlinien[i].selected = i === index;
  }
}

function determineSelectedProduktlinie(
  actionPayload: Vorbelegungdaten,
  sectionState: Produkt,
): void {
  const produktLinieFromVorvertrag = actionPayload.produktlinienname;
  const pl: Produktlinie = produktLinieFromVorvertrag
    ? sectionState.produktlinien.find(
        produktlinie => produktlinie.produktName === produktLinieFromVorvertrag,
      )
    : undefined;
  if (pl) {
    // Default PL auf false
    const plKomfort: Produktlinie = sectionState.produktlinien.find(
      produktlinie => produktlinie.produktName === Produktlinienname.KOMFORT,
    );
    if (plKomfort) {
      plKomfort.selected = false;
    }
    pl.selected = true;
    pl.gefahren.forEach(value => (value.isSelected = false));
  }
  sectionState.produktlinien[0].preisverzeichnis = actionPayload.preisverzeichnis;
}

function setBeitragFromVorvertrag(
  sectionState: Produkt,
  baustein: VorvertragBaustein<TaasTarifrisikoschluessel>,
): void {
  const leistung: Leistung = sectionState.produktlinien
    .find(produktlinie => produktlinie.selected === true)
    ?.leistungen?.find(leistung => leistung.name === baustein.taasTarifrisikoschluessel);
  if (
    leistung &&
    leistung?.eigenschaft?.toString() === LeistungEigenschaft.EXKLUSIVE &&
    Number(baustein.betrag) <= 0
  ) {
    leistung.eigenschaft = LeistungEigenschaft.INKLUSIVE;
  } else if (leistung && baustein.betrag) {
    leistung.eigenschaft = baustein.betrag.toString();
  }
}

function unselectZusatzoptionenNotIncludedInVorvertrag(
  sectionState: Produkt,
  actionPayload: Vorbelegungdaten,
): void {
  // unselect per default selected zusatzoptionen, if information is provided from vorvertrag
  sectionState.produktlinien
    .find(produktlinie => produktlinie.selected === true)
    ?.zusatzoptionen.forEach(zusatzoption => {
      if (actionPayload.vorvertragBausteinList) {
        const vorbelegteZusatzOptionFound = actionPayload.vorvertragBausteinList?.find(
          vvBstein => vvBstein?.taasTarifrisikoschluessel === zusatzoption?.name,
        );
        if (!vorbelegteZusatzOptionFound) {
          zusatzoption.isSelected = false;
        }
        return zusatzoption;
      }
    });
}

function determineSelectedBausteine(actionPayload: Vorbelegungdaten, sectionState: Produkt): void {
  actionPayload.vorvertragBausteinList?.forEach(baustein => {
    const newZusatzoption: ZusatzoptionItem = sectionState.produktlinien
      .find(produktlinie => produktlinie.selected === true)
      ?.zusatzoptionen.find(
        zusatzoption => zusatzoption.name === baustein.taasTarifrisikoschluessel,
      );

    if (newZusatzoption) {
      newZusatzoption.isSelected = true;
      newZusatzoption.deltafaktor = baustein.deltafaktor;
      newZusatzoption.plzFaktor = baustein.plzFaktor;
      newZusatzoption.anzahlVorschaeden = baustein.anzahlVorschaeden;
      newZusatzoption.bausteinAnpassungsfaktor = baustein.bausteinAnpassungsfaktor;
      if (baustein.betrag) {
        newZusatzoption.eigenschaft = baustein.betrag.toString();
      }
    }
    const newGefahr: Gefahr = sectionState.produktlinien
      .find(produktlinie => produktlinie.selected === true)
      ?.gefahren.find(gefahr => gefahr.name === baustein.taasTarifrisikoschluessel);
    if (newGefahr) {
      newGefahr.isSelected = true;
      newGefahr.deltafaktor = baustein.deltafaktor;
      newGefahr.plzFaktor = baustein.plzFaktor;
      newGefahr.anzahlVorschaeden = baustein.anzahlVorschaeden;
      newGefahr.bausteinAnpassungsfaktor = baustein.bausteinAnpassungsfaktor;
    }
    setBeitragFromVorvertrag(sectionState, baustein);
    unselectZusatzoptionenNotIncludedInVorvertrag(sectionState, actionPayload);
  });
}

export function updateZusatzoptionen(
  produktlinie: Produktlinie,
  zusatzoptionName: string,
  isSelected: boolean,
): Produktlinie {
  const newTarifItem = cloneDeep(produktlinie);
  newTarifItem.zusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
    const newZusatzoption: ZusatzoptionItem = zusatzoption;
    if (!zusatzoption.isInclusive) {
      newZusatzoption.isSelected =
        zusatzoption.name === zusatzoptionName ? isSelected : zusatzoption.isSelected;
    }
    return newZusatzoption;
  });

  return newTarifItem;
}

export function updateZusatzoptionenAfterSave(
  produktlinie: Produktlinie,
  produktLinieMasterProduct: Produktlinie,
): Produktlinie {
  const newTarifItem = cloneDeep(produktlinie);
  newTarifItem.zusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
    const zusatzoptionFromMP = produktLinieMasterProduct?.zusatzoptionen.find(
      zusatzoptionMp => zusatzoptionMp.id === zusatzoption.id,
    );
    if (zusatzoptionFromMP) {
      zusatzoption.title = zusatzoptionFromMP.title;
      zusatzoption.icon = zusatzoptionFromMP.icon;
      zusatzoption.beschreibung = zusatzoptionFromMP.beschreibung;
      zusatzoption.anzahlWohnungen = zusatzoptionFromMP.anzahlWohnungen;
      zusatzoption.label = zusatzoptionFromMP.label;
      zusatzoption.titleprint = zusatzoptionFromMP.titleprint;
      zusatzoption.besonderheiten = zusatzoptionFromMP.besonderheiten;
      zusatzoption.schadenbeispiele = zusatzoptionFromMP.schadenbeispiele;
      zusatzoption.tkvRadio = zusatzoptionFromMP.tkvRadio;
      zusatzoption.disabledLabel = zusatzoptionFromMP.disabledLabel;
    }
    return zusatzoption;
  });

  return newTarifItem;
}

export function sortByCustomAppOrder<T extends AngebotsId>(
  order: Array<App>,
  unsortedArray: Array<T>,
): Array<T> {
  return unsortedArray.sort((a, b) => order.indexOf(a.appId) - order.indexOf(b.appId));
}
